"use client";

import { type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../utils";
import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard";
import { Button } from "../inputs/button";

interface CopyableProps {
  value: string;
  children?: ReactNode;
  className?: string;
  iconClassName?: string;
  copiedDuration?: number;
  onCopy?: () => void;
  onCopyError?: (error: Error) => void;
}

export function Copyable({
  value,
  children,
  className,
  iconClassName,
  copiedDuration,
  onCopy,
  onCopyError,
}: CopyableProps) {
  const { copied, copy } = useCopyToClipboard(value, {
    copiedDuration,
    onCopy,
    onCopyError,
  });

  return (
    <span className={cn("group flex min-w-0 max-w-full items-center gap-1.5", className)}>
      <span className="min-w-0 max-w-full flex-1">{children ?? value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy to clipboard"}
        className={cn(
          "size-5 shrink-0 rounded text-muted-foreground opacity-0 hover:text-foreground focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100",
          copied && "opacity-100 text-foreground",
          iconClassName,
        )}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
    </span>
  );
}
